import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigationType, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useSlowLoadingFallback } from "@/hooks/useSlowLoadingFallback";
import { supabase } from "@/integrations/supabase/client";
import { articleExcerpt, articleExcerptWithSingleBreak, getArticleDateLabel, getArticleSortTime, isVideoUrl, parseBlogImages, type ArticleRow } from "@/lib/contentModels";
import { thumbnailImageUrl } from "@/lib/imageCdn";
import { recuerdaPrimeraFoto } from "@/lib/primeraFoto";
import { atributosDeCarga } from "@/lib/imagePriority";
import { readLocalQueryCache, readLocalUiState, writeLocalUiState } from "@/lib/localQueryCache";
import { usePersistWindowScroll } from "@/lib/scrollRestore";
const FALLBACK_IMAGE = "/logo-640.webp";

const PAGE_SIZE = 12;
const BLOG_UI_KEY = "blog-ui-state";

type BlogUiState = {
  searchTerm: string;
  visibleCount: number;
  viewMode: "scroll" | "grid";
};

const ARTICULOS_ANCHOR_POST_KEY = "articulos-scroll-anchor:post";
const ARTICULOS_ANCHOR_OFFSET_KEY = "articulos-scroll-anchor:offsetY";

const Descubrimientos = () => {
  usePersistWindowScroll("/articulos");
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsSnapshot = searchParams.toString();
  const initialQueryParam = searchParams.get("q");
  const initialUi = readLocalUiState<BlogUiState>(BLOG_UI_KEY);

  const { data: posts, isPending, isFetching, isError } = useQuery({
    queryKey: ["articulos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articulos" as never)
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ArticleRow[];
    },
    // El build deja los articulos escritos en el HTML de esta ruta, asi que la
    // pantalla pinta sin esperar a la consulta. Medido: el contenido salia a
    // 404 ms y la consulta respondia a 380, o sea que la esperaba entera.
    initialData: () => readLocalQueryCache<ArticleRow[]>("cache:articulos"),
    initialDataUpdatedAt: 0,
  });

  const list = posts ?? [];
  const [searchTerm, setSearchTerm] = useState(initialQueryParam ?? initialUi?.searchTerm ?? "");
  const [viewMode, setViewMode] = useState<"scroll" | "grid">(initialUi?.viewMode ?? "scroll");
  const [visibleCount, setVisibleCount] = useState(() => {
    const raw = initialUi?.visibleCount ?? PAGE_SIZE;
    return Number.isFinite(raw) ? Math.max(PAGE_SIZE, raw) : PAGE_SIZE;
  });
  const hasHydratedUiRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const showLoading = isPending || isFetching;
  const slowLoad = useSlowLoadingFallback(showLoading, isError);
  const [pendingAnchorPostNum, setPendingAnchorPostNum] = useState<number | null>(null);
  const pendingAnchorOffsetRef = useRef<number | null>(null);
  const hasStartedAnchorRestoreRef = useRef(false);
  const emptyMessage = isError
    ? "No se ha podido cargar los articulos."
    : searchTerm.trim()
      ? "No hay coincidencias con la búsqueda."
      : "Pronto habra articulos.";

  useEffect(() => {
    hasHydratedUiRef.current = true;
  }, []);

  useEffect(() => {
    const qParam = searchParams.get("q");
    if ((qParam ?? "") !== searchTerm) {
      setSearchTerm(qParam ?? "");
    }
  }, [searchParamsSnapshot]);

  const filtered = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return list
      .filter((post) => {
        if (!normalizedSearch) return true;
        const haystack = [post.titulo, post.descripcion ?? ""].join(" ").toLowerCase();
        return haystack.includes(normalizedSearch);
      })
      .sort((a, b) => getArticleSortTime(b) - getArticleSortTime(a));
  }, [list, searchTerm]);

  // Derivados de lista/estado (deben declararse ANTES de usarse en deps de hooks).
  const safeVisibleCount = Math.min(Math.max(visibleCount, PAGE_SIZE), Math.max(filtered.length, PAGE_SIZE));
  const visiblePosts = filtered.slice(0, safeVisibleCount);
  const hasMore = visiblePosts.length < filtered.length;

  // Para que la proxima visita pida ya la primera portada (src/lib/primeraFoto.ts).
  // Las dos vistas piden la misma URL, asi que no depende de cual este puesta.
  const primeraPortada = visiblePosts[0] ? (parseBlogImages(visiblePosts[0].imagenes)[0] ?? null) : null;
  useEffect(() => {
    if (primeraPortada && !isVideoUrl(primeraPortada)) recuerdaPrimeraFoto(thumbnailImageUrl(primeraPortada));
  }, [primeraPortada]);

  // Si volvemos desde un detalle (POP), intentamos restaurar por ancla (post + offset)
  // para que sea exacto aunque cambie la altura por imágenes/infinite.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (navigationType !== "POP") return;
    if (hasStartedAnchorRestoreRef.current) return;
    const rawPost = window.sessionStorage.getItem(ARTICULOS_ANCHOR_POST_KEY);
    if (!rawPost) return;
    const postNum = Number(rawPost);
    if (!Number.isFinite(postNum)) return;
    const rawOffset = window.sessionStorage.getItem(ARTICULOS_ANCHOR_OFFSET_KEY);
    const offset = rawOffset ? Number(rawOffset) : null;
    pendingAnchorOffsetRef.current = Number.isFinite(offset as number) ? (offset as number) : null;
    hasStartedAnchorRestoreRef.current = true;
    setPendingAnchorPostNum(postNum);
  }, [navigationType]);

  // Asegurar que el item ancla está renderizado (subimos visibleCount si hace falta).
  useEffect(() => {
    if (pendingAnchorPostNum == null) return;
    if (filtered.length === 0) return;
    const idx = filtered.findIndex((p) => p.post_num === pendingAnchorPostNum);
    if (idx < 0) return;
    setVisibleCount((prev) => Math.max(prev, Math.ceil((idx + 1) / PAGE_SIZE) * PAGE_SIZE));
  }, [pendingAnchorPostNum, filtered]);

  // Saltar al item ancla de forma estable (sin “ver” el scroll).
  useLayoutEffect(() => {
    if (pendingAnchorPostNum == null) return;
    if (typeof window === "undefined") return;
    const el = document.getElementById(`articulos-post-${pendingAnchorPostNum}`);
    if (!el) return;

    const targetOffset = pendingAnchorOffsetRef.current;
    let frameId = 0;
    let stablePasses = 0;
    let lastAbsTop = -1;
    let lastHeight = -1;

    const jump = () => {
      const rect = el.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const root = document.documentElement;
      const previousBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      if (Number.isFinite(targetOffset as number) && (targetOffset as number) > 0) {
        window.scrollTo(0, Math.max(0, absoluteTop - (targetOffset as number)));
      } else {
        // Fallback: dejarlo justo bajo la navbar.
        const headerOffset = 88;
        window.scrollTo(0, Math.max(0, absoluteTop - headerOffset - 16));
      }
      root.style.scrollBehavior = previousBehavior;
    };

    const check = () => {
      const rect = el.getBoundingClientRect();
      const absTop = Math.round(rect.top + window.scrollY);
      const height = Math.round(rect.height);
      if (absTop === lastAbsTop && height === lastHeight) stablePasses += 1;
      else stablePasses = 0;
      lastAbsTop = absTop;
      lastHeight = height;
      jump();
      if (stablePasses >= 2) {
        try {
          window.sessionStorage.removeItem(ARTICULOS_ANCHOR_POST_KEY);
          window.sessionStorage.removeItem(ARTICULOS_ANCHOR_OFFSET_KEY);
        } catch {
          /* ignore */
        }
        setPendingAnchorPostNum(null);
        return;
      }
      frameId = window.requestAnimationFrame(check);
    };

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        stablePasses = 0;
        if (frameId) window.cancelAnimationFrame(frameId);
        frameId = window.requestAnimationFrame(check);
      });
      ro.observe(el);
    }
    frameId = window.requestAnimationFrame(check);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      ro?.disconnect();
    };
  }, [pendingAnchorPostNum, safeVisibleCount, viewMode]);

  useEffect(() => {
    if (!hasHydratedUiRef.current) return;
    setVisibleCount(PAGE_SIZE);
  }, [searchTerm]);

  useEffect(() => {
    if (!hasHydratedUiRef.current) return;
    writeLocalUiState<BlogUiState>(BLOG_UI_KEY, {
      searchTerm,
      visibleCount: Math.max(PAGE_SIZE, visibleCount),
      viewMode,
    });
  }, [searchTerm, visibleCount, viewMode]);

  useEffect(() => {
    if (!hasHydratedUiRef.current) return;
    const next = new URLSearchParams(searchParams);
    if (searchTerm.trim()) next.set("q", searchTerm);
    else next.delete("q");
    if (next.toString() === searchParamsSnapshot) return;
    setSearchParams(next, { replace: true });
  }, [searchTerm, searchParamsSnapshot, searchParams, setSearchParams]);

  const loadNextChunk = useCallback(() => {
    setVisibleCount((count) => Math.min(count + PAGE_SIZE, filtered.length));
  }, [filtered.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || isPending) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        setLoadingMore(true);
        if (loadMoreTimer.current) clearTimeout(loadMoreTimer.current);
        loadMoreTimer.current = setTimeout(() => {
          loadNextChunk();
          setLoadingMore(false);
        }, 200);
      },
      { root: null, rootMargin: "400px", threshold: 0 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (loadMoreTimer.current) clearTimeout(loadMoreTimer.current);
    };
  }, [hasMore, isPending, loadNextChunk, safeVisibleCount]);

  return (
    <div className="mx-auto max-w-[80rem] px-5 py-10 md:px-9">
      <div className="mb-10">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
          <div>
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">Articulos</h1>
            <p className="mt-1 text-sm text-muted-foreground">Historias con voz propia.</p>
          </div>
          <div className="flex w-full flex-col gap-2 md:w-auto md:min-w-[34rem] md:items-end">
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por titulo o texto..."
                className="h-11 w-full rounded-lg border border-[#13151c]/24 bg-background px-4 text-sm text-foreground outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 sm:max-w-[20rem]"
                aria-label="Buscar articulos"
              />
              <div className="grid w-full grid-cols-2 gap-1 rounded-lg border border-border bg-muted/40 p-0.5 sm:w-[7.5rem]">
                {(["grid", "scroll"] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      if (mode === viewMode) return;
                      // En artículos, cambiar de vista debe resetear a arriba del todo.
                      try {
                        window.sessionStorage.removeItem(ARTICULOS_ANCHOR_POST_KEY);
                        window.sessionStorage.removeItem(ARTICULOS_ANCHOR_OFFSET_KEY);
                      } catch {
                        /* ignore */
                      }
                      const root = document.documentElement;
                      const previousBehavior = root.style.scrollBehavior;
                      root.style.scrollBehavior = "auto";
                      window.scrollTo(0, 0);
                      root.style.scrollBehavior = previousBehavior;
                      setViewMode(mode);
                    }}
                    className={`flex h-11 items-center justify-center rounded-md px-2 ${
                      viewMode === mode ? "bg-foreground text-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                    }`}
                    aria-label={mode === "grid" ? "Vista grid" : "Vista scroll"}
                    aria-pressed={viewMode === mode}
                  >
                    {mode === "grid" ? (
                      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden>
                        <rect x="2.5" y="2.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                        <rect x="11.5" y="2.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                        <rect x="2.5" y="11.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                        <rect x="11.5" y="11.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden>
                        <rect x="3" y="3.2" width="14" height="3.2" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                        <rect x="3" y="8.4" width="14" height="3.2" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                        <rect x="3" y="13.6" width="14" height="3.2" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <Link to="/blogs" className="mt-1.5 inline-block self-end text-sm font-medium text-primary hover:underline">
              ¿Quieres ver el blog retro de Popistant? {"->"}
            </Link>
          </div>
        </div>
      </div>

      {visiblePosts.length > 0 ? (
        viewMode === "scroll" ? (
          <div className="divide-y divide-border">
            {visiblePosts.map((post, idx) => {
              const rawImage = parseBlogImages(post.imagenes)[0] ?? null;
              const image = rawImage && !isVideoUrl(rawImage) ? rawImage : FALLBACK_IMAGE;
              return (
                <article key={post.post_num} id={`articulos-post-${post.post_num}`} className="py-6 first:pt-0">
                  <div className="grid gap-4 sm:grid-cols-[180px_1fr] sm:items-stretch">
                    <Link
                      to={`/post/articulos/${post.post_num}`}
                      className="block h-full"
                      onClick={(e) => {
                        try {
                          window.sessionStorage.setItem(ARTICULOS_ANCHOR_POST_KEY, String(post.post_num));
                          window.sessionStorage.setItem(ARTICULOS_ANCHOR_OFFSET_KEY, String(e.currentTarget.getBoundingClientRect().top));
                        } catch {
                          /* ignore */
                        }
                      }}
                    >
                      <div className="h-full min-h-[152px] overflow-hidden rounded-lg border border-border bg-muted">
                        <img
                          src={thumbnailImageUrl(image)}
                          alt={post.titulo}
                          className="h-full w-full object-cover object-center"
                          {...atributosDeCarga(idx === 0)}
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = FALLBACK_IMAGE;
                          }}
                        />
                      </div>
                    </Link>
                    <Link
                      to={`/post/articulos/${post.post_num}`}
                      className="group block"
                      onClick={(e) => {
                        try {
                          window.sessionStorage.setItem(ARTICULOS_ANCHOR_POST_KEY, String(post.post_num));
                          window.sessionStorage.setItem(ARTICULOS_ANCHOR_OFFSET_KEY, String(e.currentTarget.getBoundingClientRect().top));
                        } catch {
                          /* ignore */
                        }
                      }}
                    >
                      <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                        {getArticleDateLabel(post)}
                      </span>
                      <h2 className="mt-2 font-display text-xl font-bold text-foreground transition-colors group-hover:text-primary md:text-2xl">{post.titulo}</h2>
                      {post.descripcion && (
                        <p className="mt-3 line-clamp-4 whitespace-pre-line text-sm leading-6 text-muted-foreground">{articleExcerptWithSingleBreak(post.descripcion)}</p>
                      )}
                      <span className="mt-3 inline-block text-xs font-medium text-primary group-hover:underline">
                        Leer mas {"->"}
                      </span>
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visiblePosts.map((post, idx) => {
              const rawImage = parseBlogImages(post.imagenes)[0] ?? null;
              const image = rawImage && !isVideoUrl(rawImage) ? rawImage : FALLBACK_IMAGE;
              return (
                <Link
                  key={post.post_num}
                  to={`/post/articulos/${post.post_num}`}
                  className="group overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-md"
                  id={`articulos-post-${post.post_num}`}
                  onClick={(e) => {
                    try {
                      window.sessionStorage.setItem(ARTICULOS_ANCHOR_POST_KEY, String(post.post_num));
                      window.sessionStorage.setItem(ARTICULOS_ANCHOR_OFFSET_KEY, String(e.currentTarget.getBoundingClientRect().top));
                    } catch {
                      /* ignore */
                    }
                  }}
                >
                  <img
                    src={thumbnailImageUrl(image)}
                    alt={post.titulo}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    {...atributosDeCarga(idx === 0)}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />
                  <div className="p-3 sm:p-4">
                    <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{getArticleDateLabel(post)}</span>
                    <h3 className="mt-1 line-clamp-2 font-display text-[13px] font-bold leading-snug text-foreground sm:text-base">{post.titulo}</h3>
                    {post.descripcion && <p className="mt-1 line-clamp-2 text-[12px] text-muted-foreground sm:text-sm">{articleExcerpt(post.descripcion)}</p>}
                  </div>
                </Link>
              );
            })}
          </div>
        )
      ) : showLoading && !slowLoad ? (
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="grid gap-4 sm:grid-cols-[180px_1fr]">
              <div className="aspect-[4/3] animate-pulse rounded-lg bg-muted" />
              <div className="space-y-3 py-1">
                <div className="h-3 w-24 animate-pulse rounded bg-muted" />
                <div className="h-7 w-2/3 animate-pulse rounded bg-muted" />
                <div className="h-4 w-full animate-pulse rounded bg-muted" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-dashed border-border px-6 py-16 text-center">
          <p className="font-mono text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      )}

      {!isPending && hasMore && (
        <div ref={sentinelRef} className="flex min-h-[72px] flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
          {loadingMore ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <p className="text-xs">Cargando mÃ¡s artÃ­culos...</p>
            </>
          ) : (
            <p className="text-xs">Desliza para cargar mÃ¡s</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Descubrimientos;
